import { FC, useEffect, useState } from 'react';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import StyledGridContainer, {
  CenteredCellBox,
} from 'src/components/StyledComponents/StyledGridContainer';
import Status from 'src/components/Status';
import CustomNoRowsOverlay from 'src/components/StyledComponents/CustomEmptyTableRow';
import { useGetCustomer } from 'src/services/customer.service';
import { Customer } from 'src/models/Management.interface';
import LoadingTable from 'src/components/StyledComponents/CustomLoadingTable';
import StyledDataGrid from 'src/components/StyledComponents/StyledDataGrid';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import CustomerActionMenuButton from 'src/components/ActionButtons/CustomerActionMenu';
import PaginationControl from 'src/components/StyledComponents/StyledPagination';
import { loadData } from 'src/utils/grid.utils';
import { pagination } from 'src/utils/properties.utils';

const CustomerGrid: FC<{ state: boolean; searchTerm: string }> = ({
  state,
  searchTerm,
}) => {
  const { showSnackbar } = StyledSnackbar();
  const [page, setPage] = useState<number>(pagination.DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState<number>(
    pagination.DEFAULT_PAGE_SIZE
  );
  const { loading, customers, fetchCustomers, deleteCustomer, totalItems } =
    useGetCustomer();
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'name', sort: 'asc' },
  ]);
  const [sortBy, setSortBy] = useState<string>('name');
  const [order, setOrder] = useState<'ASC' | 'DESC'>('ASC');

  useEffect(() => {
    loadData(
      fetchCustomers,
      showSnackbar,
      page,
      pageSize,
      searchTerm,
      sortBy,
      order
    );
  }, [state, pageSize, searchTerm, page, sortBy, order]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
    const sortBy = newSortModel[0]?.field || 'name';
    const order = (newSortModel[0]?.sort || 'asc').toUpperCase();
    setPage(1);
    setSortBy(sortBy);
    setOrder(order as 'ASC' | 'DESC');
  };

  const columns: GridColDef[] = [
    {
      field: 'number',
      headerName: '#',
      flex: 0.3,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
    },
    { field: 'name', headerName: 'Name', flex: 0.75 },
    {
      field: 'customerCode',
      headerName: 'Code',
      flex: 0.5,
    },
    { field: 'organizationName', headerName: 'Organization', flex: 1 },
    {
      field: 'telephoneNo',
      headerName: 'Telephone Number',
      flex: 0.75,
      sortable: false,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.5,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <CenteredCellBox>
          <Status status={params.value} />
        </CenteredCellBox>
      ),
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 0.5,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <CustomerActionMenuButton
          rowId={params.id}
          customerCode={params.row.customerCode}
          status={params.row.status}
          suspensionReason={params.row.suspensionReason}
          fetchCustomers={fetchCustomers}
          deleteCustomer={deleteCustomer}
        />
      ),
    },
  ];

  const transformData = (customers: Customer[]) => {
    try {
      return customers.map((customer, index) => ({
        id: customer.id,
        number: (page - 1) * pageSize + index + 1,
        name: customer.name,
        customerCode: customer.customerCode,
        telephoneNo: customer.telephoneNo,
        organizationName: customer.organizationName,
        email: customer.email,
        status: customer.status,
        suspensionReason: customer.suspensionReason || '',
      }));
    } catch (error) {
      showSnackbar('Error transforming customer data', 'error');
      return [];
    }
  };

  const paginatedRows = transformData(customers);

  return loading ? (
    <LoadingTable columns={columns} />
  ) : (
    <StyledGridContainer
      pagination={
        totalItems > 0 ? (
          <PaginationControl
            totalItems={totalItems}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            loading={loading}
          />
        ) : undefined
      }
    >
      <StyledDataGrid
        rows={paginatedRows}
        columns={columns}
        disableColumnMenu
        sortModel={sortModel}
        sortingMode="server"
        sortingOrder={['asc', 'desc']}
        onSortModelChange={handleSortModelChange}
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
        }}
      />
    </StyledGridContainer>
  );
};

export default CustomerGrid;
